<div class="tw-flex tw-flex-col tw-gap-4">
  <div
    *ngFor="let accountWithTrans of accountsWithTransactions"
    class="tw-flex tw-flex-col tw-gap-2 tw-rounded-lg tw-bg-dt-surface-card tw-px-1 tw-py-2"
  >
    <!-- Account Name -->
    <div
      class="tw-px-2 tw-text-sm tw-font-medium tw-leading-[1.25] tw-tracking-normal min-xl:tw-text-base"
    >
      {{ accountWithTrans.account.name }}
      <span *ngIf="isManuallyAdded(accountWithTrans.account)" class="tw-text-dt-text-secondary">
        ({{ "manuallyAdded" | i18n }})
      </span>
    </div>

    <!-- Foldable transactions: toggle + content are sibling white boxes with a 4px gap -->
    <div class="tw-flex tw-flex-col tw-gap-1">
      <!-- Foldable header -->
      <button
        type="button"
        (click)="toggleExpanded(accountWithTrans.account.id)"
        class="tw-flex tw-h-[32px] tw-w-full tw-items-center tw-justify-between tw-rounded-lg tw-bg-white tw-pl-2 tw-pr-2 tw-text-left min-xl:tw-h-[40px] min-xl:tw-pl-3 min-xl:tw-pr-1.5"
      >
        <span class="tw-flex tw-items-center tw-text-dt-text-secondary">
          <span
            class="tw-text-[14px] tw-uppercase tw-leading-[1.25] tw-tracking-[0.072px] min-xl:tw-text-[16px]"
            >{{ "transactions" | i18n }}</span
          >
          <span
            class="tw-text-[14px] tw-font-medium tw-leading-[1.25] tw-tracking-normal tw-text-dt-text-headings min-xl:tw-text-[16px]"
            >({{ accountWithTrans.transactions.length }})</span
          >
        </span>
        <icon [name]="accountWithTrans.expanded ? 'up-arrow' : 'down-arrow'" [size]="6"></icon>
      </button>

      <!-- Foldable content: desktop = shared grid (aligned columns); mobile = stacked
           flex rows that wrap (description on line 1, the rest on line 2) -->
      <div
        *ngIf="accountWithTrans.expanded"
        class="tw-flex tw-flex-col tw-gap-2 tw-rounded-lg tw-bg-white tw-px-2 tw-py-3 tw-transition-all tw-duration-200 tw-ease-in-out min-xl:tw-grid min-xl:tw-grid-cols-[minmax(0,2fr)_auto_minmax(100px,1.2fr)_auto_auto_auto_minmax(0,1fr)_auto_auto_auto] min-xl:tw-items-center min-xl:tw-gap-x-2 min-xl:tw-gap-y-2"
      >
        <!-- Column headers (desktop ≥xl: joins the shared grid via contents) -->
        <div
          class="tw-hidden tw-text-[11px] tw-uppercase tw-leading-[1.25] tw-tracking-[0.2px] tw-text-dt-text-secondary min-xl:tw-contents"
        >
          <span>{{ "description" | i18n }}</span>
          <span></span>
          <span>{{ "date" | i18n }}</span>
          <span></span>
          <span>{{ "inOut" | i18n }}</span>
          <span></span>
          <span>{{ "amount" | i18n }}</span>
          <span></span>
          <span>{{ "symbol" | i18n }}</span>
          <span></span>
        </div>

        <!-- Column headers (stacked <xl: two-line legend, matches Figma mobile) -->
        <div
          class="tw-flex tw-flex-col tw-gap-0.5 tw-border-0 tw-border-b tw-border-neutral-100 tw-pb-1 tw-text-[13px] tw-uppercase tw-leading-[1.25] tw-tracking-[0.1625px] tw-text-dt-text-secondary min-xl:tw-hidden"
        >
          <span>{{ "description" | i18n }}</span>
          <div class="tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-4">
            <div class="tw-flex tw-w-full tw-items-center tw-gap-2">
              <span>{{ "date" | i18n }}</span>
              <span>{{ "inOut" | i18n }}</span>
              <span>{{ "amount" | i18n }}</span>
              <span>{{ "symbol" | i18n }}</span>
            </div>
            <span>{{ "edit" | i18n }}</span>
          </div>
        </div>

        <!-- Saved transactions -->
        <div
          *ngFor="let transaction of accountWithTrans.transactions; let i = index"
          class="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 tw-border-0 tw-border-b tw-border-neutral-100 tw-pb-2 tw-text-[13px] tw-leading-[1.25] tw-tracking-[0.175px] min-xl:tw-text-sm min-xl:tw-contents"
        >
          <!-- Description + its trailing "/" share line 1 on mobile so the date below
               lines up under the description; on desktop they rejoin the shared grid. -->
          <span class="tw-flex tw-w-full tw-items-center tw-gap-x-2 min-xl:tw-contents">
            <span class="tw-min-w-0 tw-truncate tw-underline">{{
              transaction.formData.description
            }}</span>
            <span class="tw-shrink-0 tw-text-dt-text-secondary">/</span>
          </span>
          <span class="tw-underline">{{ transaction.formData.date }}</span>
          <span class="tw-text-dt-text-secondary">/</span>
          <span
            class="tw-font-medium"
            [ngClass]="
              transaction.formData.type === TransactionDirection.In
                ? 'tw-text-success-600'
                : 'tw-text-danger'
            "
          >
            {{ (transaction.formData.type === TransactionDirection.In ? "in" : "out") | i18n }}
          </span>
          <span class="tw-text-dt-text-secondary">/</span>
          <span class="tw-underline">{{ transaction.formData.amount }}</span>
          <span class="tw-text-dt-text-secondary">/</span>
          <span class="tw-uppercase tw-text-dt-text-secondary">{{
            transaction.formData.symbol || transaction.formData.currency
          }}</span>
          <button
            type="button"
            (click)="removeTransaction(accountWithTrans.account.id, i)"
            class="tw-ml-auto tw-bg-transparent tw-text-[13px] tw-font-medium tw-leading-[1.25] tw-tracking-[0.13px] tw-text-danger hover:tw-underline min-xl:tw-ml-0 min-xl:tw-justify-self-end"
          >
            {{ "delete" | i18n }}
          </button>
        </div>

        <!-- Add transaction row -->
        <form
          [formGroup]="getFormForAccount(accountWithTrans.account.id)"
          (ngSubmit)="addTransaction(accountWithTrans.account.id)"
          class="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1 min-xl:tw-contents"
        >
          <!-- Description + its trailing "/" share line 1 on mobile; on desktop the wrapper
               dissolves so the input and "/" rejoin the shared grid as separate cells. -->
          <div class="tw-flex tw-w-full tw-items-center tw-gap-2 min-xl:tw-contents">
            <app-input
              class="tw-w-full min-xl:tw-w-auto"
              [compact]="true"
              [value]="getFormForAccount(accountWithTrans.account.id).controls['description'].value"
              [placeholder]="'description' | i18n"
              [isRequired]="true"
              [hasError]="
                getFormForAccount(accountWithTrans.account.id).controls['description'].invalid &&
                getFormForAccount(accountWithTrans.account.id).controls['description'].touched
              "
              (outputValue)="
                getFormForAccount(accountWithTrans.account.id).controls['description'].setValue(
                  $event
                )
              "
            ></app-input>
            <span class="tw-text-dt-text-secondary">/</span>
          </div>

          <!-- Date -->
          <app-date-picker
            [compact]="true"
            [dateText]="getFormForAccount(accountWithTrans.account.id).controls['date'].value"
            [isRequired]="true"
            [showErrorText]="false"
            [hasError]="
              getFormForAccount(accountWithTrans.account.id).controls['date'].invalid &&
              getFormForAccount(accountWithTrans.account.id).controls['date'].touched
            "
            (changeDateEvent)="
              getFormForAccount(accountWithTrans.account.id).controls['date'].setValue($event)
            "
          ></app-date-picker>
          <span class="tw-text-dt-text-secondary">/</span>

          <!-- Direction (In/Out) -->
          <app-dropdown-select
            [compact]="true"
            [placeholder]="'inOut' | i18n"
            [selectedOption]="selectedDirectionLabel(accountWithTrans.account.id)"
            [options]="directionOptions"
            (optionSelected)="onDirectionSelected(accountWithTrans.account.id, $event)"
          ></app-dropdown-select>
          <span class="tw-text-dt-text-secondary">/</span>

          <!-- Amount -->
          <app-input
            [compact]="true"
            [value]="getFormForAccount(accountWithTrans.account.id).controls['amount'].value"
            [placeholder]="'0.00'"
            [isRequired]="true"
            [hasError]="
              getFormForAccount(accountWithTrans.account.id).controls['amount'].invalid &&
              getFormForAccount(accountWithTrans.account.id).controls['amount'].touched
            "
            [type]="'number'"
            (outputValue)="
              getFormForAccount(accountWithTrans.account.id).controls['amount'].setValue($event)
            "
          ></app-input>
          <span class="tw-text-dt-text-secondary">/</span>

          <!-- Symbol (account currency, read-only) -->
          <span class="tw-uppercase tw-text-dt-text-secondary">{{
            accountWithTrans.account.currency
          }}</span>

          <!-- Submit -->
          <button
            type="submit"
            class="tw-ml-auto tw-bg-transparent tw-text-[13px] tw-font-medium tw-leading-[1.25] tw-tracking-[0.13px] tw-text-primary hover:tw-underline min-xl:tw-ml-0 min-xl:tw-justify-self-end"
          >
            {{ "submit" | i18n }}
          </button>
        </form>
      </div>
    </div>
  </div>
</div>
